import {
  Alert,
  Anchor,
  Button,
  type ButtonProps,
  Collapse,
  Divider,
  PinInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconAlertCircle,
  IconKey,
  IconMail,
  IconShieldCheck,
  IconShieldLock,
} from '@tabler/icons-react';
import type { TFunction } from 'i18next';
import { Fragment, type ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type BrowserLoginSuccess,
  decodeNostrPublicKey,
  LOGIN_METHOD,
  type LoginChallenge,
  type LoginChallengeResult,
  type LoginChallengeUserResponse,
  type LoginRequest,
  type Verify2FALoginRequest,
} from '@/api';
import { primaryMethod } from './login-methods';

type Translate = TFunction<'translation', undefined>;

// Mantine buttons have a fixed height and nowrap labels; let long translations wrap.
const methodButtonProps: ButtonProps = {
  fullWidth: true,
  size: 'md',
  styles: {
    root: { height: 'auto', minHeight: 44, paddingBlock: 8 },
    label: { whiteSpace: 'normal', textAlign: 'center' },
  },
};

function backendMessage(e: unknown): string | null {
  const data = (e as any)?.response?.data;
  if (data && typeof data === 'object' && typeof data.message === 'string' && data.message) {
    return data.message;
  }
  return null;
}

interface ExplainedMethodProps {
  button: ReactNode;
  toggleLabel: string;
  explanation: string;
  defaultOpen: boolean;
  status?: string;
}

function ExplainedMethod({
  button,
  toggleLabel,
  explanation,
  defaultOpen,
  status,
}: ExplainedMethodProps) {
  const id = useId();
  const [toggled, setToggled] = useState<boolean | null>(null);
  const open = toggled ?? defaultOpen;

  return (
    <Stack gap={6}>
      {button}
      {status && (
        <Text size="sm" c="dimmed" role="status">
          {status}
        </Text>
      )}
      <Anchor
        component="button"
        type="button"
        size="sm"
        ta="left"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setToggled(!open)}
      >
        {toggleLabel}
      </Anchor>
      <Collapse in={open}>
        <Text id={id} size="sm" c="dimmed">
          {explanation}
        </Text>
      </Collapse>
    </Stack>
  );
}

interface MethodListProps {
  methods: LOGIN_METHOD[];
  oidcDisplayName?: string | null;
  initialEmail: string;
  busyMethod: LOGIN_METHOD | null;
  nostrLogin: () => void;
  oidcLogin: () => void;
  magicLinkLogin: (email: string) => void;
  t: Translate;
}

function MethodList({
  methods,
  oidcDisplayName,
  initialEmail,
  busyMethod,
  nostrLogin,
  oidcLogin,
  magicLinkLogin,
  t,
}: MethodListProps) {
  const form = useForm({
    initialValues: { email: initialEmail },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : t('login.invalidEmail')),
    },
  });

  if (methods.length === 0) {
    return <Text>{t('login.unavailable')}</Text>;
  }

  const primary = primaryMethod(methods);
  const variantFor = (m: LOGIN_METHOD) => (m === primary ? 'filled' : 'outline');
  const busyProps = (m: LOGIN_METHOD) => ({
    loading: busyMethod === m,
    disabled: busyMethod !== null && busyMethod !== m,
  });

  const block = (m: LOGIN_METHOD): ReactNode => {
    switch (m) {
      case LOGIN_METHOD.EMAIL_MAGIC_LINK:
        return (
          <form onSubmit={form.onSubmit(({ email }) => magicLinkLogin(email))}>
            <Stack gap="sm">
              <TextInput
                label={t('login.emailLabel')}
                placeholder={t('login.emailPlaceholder')}
                type="email"
                autoComplete="email"
                size="md"
                {...form.getInputProps('email')}
              />
              <Button
                type="submit"
                variant={variantFor(m)}
                leftSection={<IconMail size={20} />}
                {...methodButtonProps}
                {...busyProps(m)}
              >
                {t('login.emailButton')}
              </Button>
              <Text size="sm" c="dimmed">
                {t('login.emailHelper')}
              </Text>
            </Stack>
          </form>
        );
      case LOGIN_METHOD.OIDC:
        return (
          <ExplainedMethod
            button={
              <Button
                variant={variantFor(m)}
                leftSection={<IconShieldCheck size={20} />}
                onClick={oidcLogin}
                {...methodButtonProps}
                {...busyProps(m)}
              >
                {oidcDisplayName
                  ? t('login.ssoButtonNamed', { name: oidcDisplayName })
                  : t('login.ssoButton')}
              </Button>
            }
            toggleLabel={t('login.ssoToggle')}
            explanation={t('login.ssoExplanation')}
            defaultOpen={false}
            status={busyMethod === m ? t('login.redirectingOidc') : undefined}
          />
        );
      case LOGIN_METHOD.NOSTR:
        return (
          <ExplainedMethod
            button={
              <Button
                variant={variantFor(m)}
                leftSection={<IconKey size={20} />}
                onClick={nostrLogin}
                {...methodButtonProps}
                {...busyProps(m)}
              >
                {t('login.nostrButton')}
              </Button>
            }
            toggleLabel={t('login.nostrToggle')}
            explanation={t('login.nostrExplanation')}
            defaultOpen={methods.length === 1}
            status={busyMethod === m ? t('login.nostrInstructions') : undefined}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Stack gap="md">
      {methods.map((m, i) => (
        <Fragment key={m}>
          {i === 1 && <Divider label={t('login.orDivider')} labelPosition="center" />}
          {block(m)}
        </Fragment>
      ))}
    </Stack>
  );
}

interface EmailSentProps {
  email: string;
  onBack: () => void;
  t: Translate;
}

function EmailSent({ email, onBack, t }: EmailSentProps) {
  return (
    <Stack gap="md">
      <Title order={1} size="h3" fw={600}>
        {t('login.checkInboxTitle')}
      </Title>
      <Text>{t('login.checkInboxBody', { email })}</Text>
      <Button variant="default" onClick={onBack} {...methodButtonProps}>
        {t('login.useDifferentEmail')}
      </Button>
    </Stack>
  );
}

interface StepTwoFactorProps {
  pendingToken: string;
  verify2FA: (request: Verify2FALoginRequest) => Promise<BrowserLoginSuccess>;
  loginDoneCb: () => void;
  onBack: () => void;
  setError: (error: string | null) => void;
  t: Translate;
}

function StepTwoFactor({
  pendingToken,
  verify2FA,
  loginDoneCb,
  onBack,
  setError,
  t,
}: StepTwoFactorProps) {
  const [code, setCode] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const handleVerify = async () => {
    if (code.length < 6) return;

    setIsBusy(true);
    setError(null);

    try {
      await verify2FA({
        pending_token: pendingToken,
        code: code,
      });
      loginDoneCb();
    } catch (e: any) {
      console.error(e);
      const message = backendMessage(e);
      if (message) {
        setError(message);
      } else if (e?.response?.status === 401) {
        setError(t('login.invalid2FACode'));
      } else if (e?.response?.status === 429) {
        setError(t('login.tooMany2FAAttempts'));
        onBack();
      } else {
        setError(t('login.genericError'));
      }
      setCode('');
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Stack gap="md" align="center">
      <IconShieldLock size={48} stroke={1.5} />
      <Text size="lg" fw={500}>
        {t('login.twoFactorRequired')}
      </Text>
      <Text size="sm" c="dimmed" ta="center">
        {t('login.enterTwoFactorCode')}
      </Text>
      <PinInput
        length={6}
        type="number"
        value={code}
        onChange={setCode}
        oneTimeCode
        disabled={isBusy}
        size="lg"
      />
      <Button
        onClick={handleVerify}
        loading={isBusy}
        disabled={code.length < 6}
        {...methodButtonProps}
      >
        {t('login.verify')}
      </Button>
      <Text size="sm" c="dimmed" ta="center">
        {t('login.useRecoveryCode')}
      </Text>
      <Button variant="subtle" size="xs" onClick={onBack}>
        {t('login.back')}
      </Button>
    </Stack>
  );
}

export interface LoginProps {
  mode: 'signin' | 'signup';
  methods: LOGIN_METHOD[];
  oidcDisplayName?: string | null;
  login: (loginRequest: LoginRequest) => Promise<LoginChallenge>;
  loginChallenge: (props: LoginChallengeUserResponse) => Promise<LoginChallengeResult>;
  verify2FA: (request: Verify2FALoginRequest) => Promise<BrowserLoginSuccess>;
  loginDoneCb: () => void;
}

type Step = 'choose' | 'emailSent' | 'twoFactor';

export function Login(props: LoginProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('choose');
  const [busyMethod, setBusyMethod] = useState<LOGIN_METHOD | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState('');
  const [pendingToken, setPendingToken] = useState<string | null>(null);

  const backToChoose = () => setStep('choose');

  const nostrLogin = async () => {
    setError(null);
    const nostr = (window as any).nostr;
    if (!nostr) {
      setError(t('login.nostrNoExtension'));
      return;
    }
    setBusyMethod(LOGIN_METHOD.NOSTR);
    try {
      const publicKey = await nostr.getPublicKey();
      if (!publicKey) {
        setError(t('login.genericError'));
        return;
      }
      const challenge = await props.login({
        type: 'NOSTR',
        content: {
          public_key: decodeNostrPublicKey(publicKey),
        },
      });
      if (challenge?.type !== 'NOSTR') {
        setError(t('login.genericError'));
        return;
      }
      const loginResult = await props.loginChallenge({
        type: 'NOSTR',
        content: {
          id: (challenge.content as any).id,
          response: await nostr.signEvent({
            kind: 22242,
            created_at: Math.floor(Date.now() / 1000),
            tags: [['challenge', (challenge.content as any).challenge]],
            content: '',
          }),
        },
      });
      if (!loginResult) {
        setError(t('login.genericError'));
        return;
      }

      if (loginResult.status === 'requires_2fa') {
        setPendingToken(loginResult.data.pending_token);
        setStep('twoFactor');
        return;
      }

      props.loginDoneCb();
    } catch (e: any) {
      console.error(e);
      const code = e?.response?.data?.error;
      // The shared challenge errors are worded for magic links.
      if (code === 'challenge_mismatch' || code === 'invalid_challenge') {
        setError(t('login.genericError'));
      } else {
        setError(backendMessage(e) ?? t('login.genericError'));
      }
    } finally {
      setBusyMethod(null);
    }
  };

  const oidcLogin = async () => {
    setBusyMethod(LOGIN_METHOD.OIDC);
    setError(null);
    try {
      const oidcChallenge = await props.login({
        type: LOGIN_METHOD.OIDC,
        content: {},
      });
      if (!oidcChallenge || oidcChallenge.type !== LOGIN_METHOD.OIDC) {
        setError(t('login.genericError'));
        setBusyMethod(null);
        return;
      }
      // Stays busy while the browser leaves for the IdP.
      window.location.href = (oidcChallenge.content as any).auth_url;
    } catch (e) {
      console.error(e);
      setError(backendMessage(e) ?? t('login.genericError'));
      setBusyMethod(null);
    }
  };

  const magicLinkLogin = async (email: string) => {
    setBusyMethod(LOGIN_METHOD.EMAIL_MAGIC_LINK);
    setError(null);
    try {
      const challenge = await props.login({
        type: LOGIN_METHOD.EMAIL_MAGIC_LINK,
        content: {
          email: email,
        },
      });
      if (!challenge || challenge.type !== LOGIN_METHOD.EMAIL_MAGIC_LINK) {
        setError(t('login.genericError'));
        return;
      }
      setSentTo(email);
      setStep('emailSent');
    } catch (e) {
      console.error(e);
      setError(backendMessage(e) ?? t('login.genericError'));
    } finally {
      setBusyMethod(null);
    }
  };

  const errorAlert = error && (
    <Alert color="red" variant="light" icon={<IconAlertCircle size={20} />} role="alert">
      {error}
    </Alert>
  );

  return (
    <Stack gap="lg">
      {step === 'choose' && (
        <>
          <Stack gap="xs">
            <Title order={1} size="h3" fw={600}>
              {props.mode === 'signup' ? t('login.signupTitle') : t('login.signinTitle')}
            </Title>
            <Text size="sm" c="dimmed">
              {props.mode === 'signup' ? t('login.signupSubtitle') : t('login.signinSubtitle')}
            </Text>
          </Stack>
          {errorAlert}
          <MethodList
            methods={props.methods}
            oidcDisplayName={props.oidcDisplayName}
            initialEmail={sentTo}
            busyMethod={busyMethod}
            nostrLogin={nostrLogin}
            oidcLogin={oidcLogin}
            magicLinkLogin={magicLinkLogin}
            t={t}
          />
        </>
      )}
      {step === 'emailSent' && <EmailSent email={sentTo} onBack={backToChoose} t={t} />}
      {step === 'twoFactor' && pendingToken && (
        <>
          {errorAlert}
          <StepTwoFactor
            pendingToken={pendingToken}
            verify2FA={props.verify2FA}
            loginDoneCb={props.loginDoneCb}
            onBack={backToChoose}
            setError={setError}
            t={t}
          />
        </>
      )}
    </Stack>
  );
}
